import { cssVars } from '@/app/styles';
import { subtypeChartLabels } from '@/constants';

const SM_BAR_THICKNESS = 18;
const LG_BAR_THICKNESS = 50;

export function constructDatasets(
  labels,
  data,
  chartType,
  datasetConfigs,
  chartName
) {
  const datasets = [];
  data.forEach((set, i) => {
    const dataset = {
      label: '',
      type: chartType,
      data: set,
      ...datasetConfigs[i],
    };
    datasets.push(dataset);
  });

  /*
   * Sort the datasets by the sum of their data points -
   * this is to ensure that the "bigger" fills appear behind the smaller ones
   */
  // sortDatasetsBySum(datasets);

  const constructedData = {
    labels,
    datasets,
    chartName,
    // datasets: sortedDatasets,
  };

  return constructedData;
}

export class ChartDataBuilder {
  constructor(data) {
    this.chartData = data;
    this.labels = this.retreiveChartLabels(this.chartData);
    this.builderOptions = null;
  }

  /**
   * Changes Date received in data into date strings formatted in a way ChartJS can use
   * them as labels
   * @param {*} dataPoints DataPoints to iterate through retreiving the formatted date strings
   */
  retreiveChartLabels = ({ dataPoints }) => {
    const dates = []; // assume no data initially
    dataPoints.forEach((point) => {
      dates.push(point.date); // build a date array
    });

    // Date.getMonth() returns an int 0 - 11. This enum is used to convert to Abbr. Month String
    const dateEnum = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const labels = dates.map((date) => {
      const dateObj = new Date(date); // Create date objects so that extraction methods can be used
      // build the date in format Month-Year ie Jan 19, Feb 19 etc...
      const newDates = `${dateEnum[dateObj.getUTCMonth()]}`;
      return newDates; // New Date String Array
    });

    return labels; // Return Labels Ready to be consumed
  };

  setBuilderOptions(options) {
    this.builderOptions = options;
  }
}

export class SingleValueDataBuilder extends ChartDataBuilder {
  constructor(data, dataType) {
    super(data);
    this.dataType = dataType;
    this.data = this.extractByValue(data);
  }

  /**
   * Extracts values that are nested one level deep
   * @param {*} datasetType CURRENT_STR_PROPERTIES, OCCUPANCY, etc
   * @param {*} dataPoints 6 points of data
   */
  extractByValue = ({ dataPoints }) => {
    const data = []; // Initially it is assumed there is no data
    dataPoints.forEach((point) => {
      // null values will add a gap in the line.. `undefined` won't
      const valueMaybeNull = point[this.dataType]
        ? point[this.dataType].value
        : null;
      data.push(valueMaybeNull); // Push values into data array
    });
    return [data];
  };
}

export class MultiValueDataBuilder extends ChartDataBuilder {
  constructor(data, dataType, extractionType) {
    super(data);
    this.dataType = dataType;
    this.extractionType = extractionType;
    this.data = this.extractMultiSets(data);
  }

  checkPercentageOverflow(data) {
    const datasets = [...data];
    if (this.extractionType === 'percentage') {
      const UNKNOWN_DATA = 0;
      const numSets = datasets.length;
      const numPoints = datasets[UNKNOWN_DATA].length;
      for (let c = 0; c < numPoints; c++) {
        let sum = 0;
        for (let r = 0; r < numSets; r++) {
          sum += datasets[r][c];
        }
        if (sum !== 100) {
          const delta = sum - 100;
          if (Math.sign(delta)) {
            datasets[UNKNOWN_DATA][c] -= delta;
          } else {
            datasets[UNKNOWN_DATA][c] += Math.abs(delta);
          }
        }
      }
    }
    return datasets;
  }

  extractMultiSets({ dataPoints }) {
    const datasets = [];
    Object.keys(subtypeChartLabels[this.dataType]).forEach((dataSubtype) => {
      const data = [];
      dataPoints.forEach((point) => {
        if (point?.[this.dataType]?.[dataSubtype]) {
          data.push(point[this.dataType][dataSubtype][this.extractionType]);
        }
      });
      datasets.push(data);
    });

    const checkedData = this.checkPercentageOverflow(datasets);
    return checkedData;
  }
}

export class CurrentStrPropertiesDataBuilder extends SingleValueDataBuilder {
  constructor(data, chartMode = null, dataType = 'currentStrProperties') {
    super(data, dataType);
    this.datasets = [];
    this.chartMode = chartMode;
    this.datasetConfigurations = CurrentStrPropertiesDataBuilder.configurations;
  }

  static get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.chartColors.currentStrPropertiesAccent,
        backgroundColor: cssVars.chartColors.currentStrPropertiesAccent,
        pointBackgroundColor: cssVars.chartColors.currentStrPropertiesAccent,
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

export class RentalSizeDataBuilder extends MultiValueDataBuilder {
  constructor(
    data,
    chartMode = null,
    dataType = 'rentalSize',
    extractionType = 'value'
  ) {
    super(data, dataType, extractionType);
    this.chartMode = chartMode;
    this.datasetConfigurations = RentalSizeDataBuilder.configurations;
  }

  static get configurations() {
    return [
      {
        fill: false,
        pointRadius: 2, // dot radius
        pointHoverRadius: 4, // dot radius on hover
        borderWidth: 2,
        borderColor: 'rgba(64, 169, 255, .8)',
        backgroundColor: 'rgba(64, 169, 255, .8)',
      },
      {
        fill: false,
        pointRadius: 2, // dot radius
        pointHoverRadius: 4, // dot radius on hover
        borderWidth: 2,
        borderColor: 'rgba(255, 122, 69, 0.8)',
        backgroundColor: 'rgba(255, 122, 69, 0.8)',
      },
      {
        fill: false,
        pointRadius: 2, // dot radius
        pointHoverRadius: 4, // dot radius on hover
        borderWidth: 2,
        borderColor: 'rgba(54, 207, 201, 0.8)',
        backgroundColor: 'rgba(54, 207, 201, 0.8)',
      },
      {
        fill: false,
        pointRadius: 2, // dot radius
        pointHoverRadius: 4, // dot radius on hover
        borderDash: [6, 5],
        borderWidth: 2,
        borderColor: 'rgba(149, 222, 100, 0.8)',
        backgroundColor: 'rgba(149, 222, 100, 0.8)',
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

export class OccupancyDataBuilder extends SingleValueDataBuilder {
  constructor(data, chartMode = null, dataType = 'occupancy') {
    super(data, dataType);
    this.chartMode = chartMode;
    this.datasetConfigurations = OccupancyDataBuilder.configurations;
  }

  static get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.chartColors.bookingsAccent,
        backgroundColor: cssVars.chartColors.bookingsAccent,
        pointBackgroundColor: cssVars.chartColors.bookingsAccent,
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

export class RevenueDataBuilder extends SingleValueDataBuilder {
  constructor(data, chartMode = null, dataType = 'revenue') {
    super(data, dataType);
    this.chartMode = chartMode;
    this.datasetConfigurations = RevenueDataBuilder.configurations;
  }

  static get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.chartColors.revenueAccent,
        backgroundColor: cssVars.chartColors.revenueAccent,
        pointBackgroundColor: cssVars.chartColors.revenueAccent,
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

export class AverageDailyRateDataBuilder extends SingleValueDataBuilder {
  constructor(data, chartMode, dataType = 'adr') {
    super(data, dataType);
    this.chartMode = chartMode;
  }

  get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.chartColors.avgDailyRateAccent,
        backgroundColor: cssVars.chartColors.avgDailyRateAccent,
        pointBackgroundColor: cssVars.chartColors.avgDailyRateAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

export class RegistrationRateDataBuilder extends SingleValueDataBuilder {
  constructor(data, chartMode, dataType = 'registrationRate') {
    super(data, dataType);
    this.chartMode = chartMode;
  }

  get configurations() {
    return [
      {
        fill: false,
        borderColor: 'rgba(54, 207, 201, 0.6)',
        backgroundColor: '#b7eb8f',
        pointBackgroundColor: 'rgba(54, 207, 201, 0.6)',
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

export class TOTDataBuilder extends SingleValueDataBuilder {
  constructor(
    data,
    chartMode = null,
    dataType = 'revenue',
    extractionType = 'value'
  ) {
    super(data, dataType, extractionType);
    this.chartMode = chartMode;
    this.datasetConfigurations = TOTDataBuilder.configurations;
  }

  static get configurations() {
    return [
      {
        fill: '+1',
        pointRadius: 0,
        borderWidth: 2,
        borderColor: '#00bfa5',
        backgroundColor: 'rgba(224, 242, 241, 0.5)',
      },
      {
        fill: '+1',
        borderWidth: 2,
        pointRadius: 0,
        borderColor: '#fdb147',
        backgroundColor: '#fdb147',
      },
    ];
  }

  get constructedDataFromRevenue() {
    // TODO: Need to get tax rate from data
    const TAX_RATE = 0.1;
    const ESTIMATED_COLLECTION = 0.85;
    const chartData = this.data.flat();
    const estimatedTax = chartData.map((point) => Math.round(point * TAX_RATE));
    // suppose the city is only getting 85% of what they should
    const dummyActualTax = estimatedTax.map((point) =>
      Math.round(point * ESTIMATED_COLLECTION)
    );
    const constructedData = [estimatedTax, dummyActualTax];
    return constructedData;
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.constructedDataFromRevenue,
      'line',
      this.datasetConfigurations
    );
  }
}

export class ResidentialTypeDataBuilder extends MultiValueDataBuilder {
  constructor(
    data,
    chartMode,
    dataType = 'residentialType',
    extractionType = 'percentage'
  ) {
    super(data, dataType, extractionType);
    this.chartMode = chartMode;
  }

  get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.color.residentialType.primary,
        backgroundColor: cssVars.color.residentialType.primary,
        pointBackgroundColor: cssVars.color.residentialType.primary,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.residentialType.secondaryInvestment,
        backgroundColor: cssVars.color.residentialType.secondaryInvestment,
        pointBackgroundColor: cssVars.color.residentialType.secondaryInvestment,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.residentialType.unknown,
        backgroundColor: cssVars.color.residentialType.unknown,
        pointBackgroundColor: cssVars.color.residentialType.unknown,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        borderDash: [8, 5],
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

export class RentalStructureDataBuilder extends MultiValueDataBuilder {
  constructor(
    data,
    chartMode,
    dataType = 'rentalStructure',
    extractionType = 'percentage'
  ) {
    super(data, dataType, extractionType);
    this.chartMode = chartMode;
  }

  get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.color.rentalStructure.mainStructureAccent,
        backgroundColor: cssVars.color.rentalStructure.mainStructureAccent,
        pointBackgroundColor: cssVars.color.rentalStructure.mainStructureAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.rentalStructure.aduAccent,
        backgroundColor: cssVars.color.rentalStructure.aduAccent,
        pointBackgroundColor: cssVars.color.rentalStructure.aduAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.rentalStructure.multifamilyAccent,
        backgroundColor: cssVars.color.rentalStructure.multifamilyAccent,
        pointBackgroundColor: cssVars.color.rentalStructure.multifamilyAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        borderDash: [8, 5],
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.rentalStructure.unknownAccent,
        backgroundColor: cssVars.color.rentalStructure.unknownAccent,
        pointBackgroundColor: cssVars.color.rentalStructure.unknownAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        borderDash: [8, 5],
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

export class RentalTypeDataBuilder extends MultiValueDataBuilder {
  constructor(
    data,
    chartMode,
    dataType = 'rentalType',
    extractionType = 'percentage'
  ) {
    super(data, dataType, extractionType);
    this.chartMode = chartMode;
  }

  get configurations() {
    return [
      {
        fill: false,
        borderColor: cssVars.color.rentalType.selfContainedAccent,
        backgroundColor: cssVars.color.rentalType.selfContainedAccent,
        pointBackgroundColor: cssVars.color.rentalType.selfContainedAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.rentalType.partialAccent,
        backgroundColor: cssVars.color.rentalType.partialAccent,
        pointBackgroundColor: cssVars.color.rentalType.partialAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        fill: false,
        borderColor: cssVars.color.rentalType.unknownAccent,
        backgroundColor: cssVars.color.rentalType.unknownAccent,
        pointBackgroundColor: cssVars.color.rentalType.unknownAccent,
        pointRadius: 3, // dot radius
        pointHoverRadius: 5, // dot radius on hover
        borderDash: [8, 5],
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

export class RentalSizeSidePanelDataBuilder extends ChartDataBuilder {
  constructor(data, chartMode = null, dataType = 'rentalSize') {
    super(data);
    this.chartMode = chartMode;
    this.dataType = dataType;
    this.datasetConfigurations = RentalSizeSidePanelDataBuilder.configurations;
    this.data = this.extractByHomeSize(data);
  }

  static get configurations() {
    return [
      {
        fill: false,
        borderColor: '#37A3BC',
        backgroundColor: '#37A3BC',
        pointBackgroundColor: '#37A3BC',
        pointRadius: 5, // dot radius
        pointHoverRadius: 3, // dot radius on hover];
      },
    ];
  }

  /**
   * Uses last point as latest data.
   * FIXME: This only works for RENTAL_SIZE in sidebar since it only requires the latest point.
   * #TODO: Need to have a different name for rental_size when using just one point.
   * @param {*} datasetType CURRENT_STR_PROPERTIES, OCCUPANCY, etc
   * @param {*} dataPoints 6 points of data
   */
  extractByHomeSize = ({ dataPoints }) => {
    const data = [];
    const latestKpi = dataPoints[dataPoints.length - 1];
    Object.values(latestKpi[this.dataType]).forEach((roomSize) => {
      data.push(roomSize.value);
    });
    return [data];
  };

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.datasetConfigurations
    );
  }
}

export class BookedRatioDataBuilder extends ChartDataBuilder {
  constructor(data, chartMode) {
    super(data);
    this.chartMode = chartMode;
    this.dataPoints = data.dataPoints;
    this.data = this.extractBookedRatioData();
  }

  get configurations() {
    return [
      {
        backgroundColor: '#c7d1f3',
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        backgroundColor: '#37A3BC',
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
      {
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        barThickness:
          this.chartMode === 'light' ? SM_BAR_THICKNESS : LG_BAR_THICKNESS,
      },
    ];
  }

  extractBookedRatioData() {
    const datasetVacant = [];
    const datasetBook = [];
    const datasetBlock = [];

    this.dataPoints.forEach((dataPoint) => {
      const { vacant, booked, blocked } = dataPoint;
      datasetVacant.push(vacant && vacant.value);
      datasetBook.push(booked && booked.value);
      datasetBlock.push(blocked && blocked.value);
    });

    const datasets = [datasetBlock, datasetBook, datasetVacant];

    return datasets;
  }

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'bar',
      this.configurations
    );
  }
}

// labels, data, chartType, datasetConfigs

export class OnlinePlatformsDataBuilder extends ChartDataBuilder {
  constructor(data, chartMode = null, dataType = 'onlinePlatforms') {
    super(data);
    this.dataType = dataType;
    this.chartMode = chartMode;
    this.datasetConfigurations = this.chartDatasetConfigs;
    this.data = this.extractOnlinePlatforms(data);
  }

  get chartDatasetConfigs() {
    const order = {
      FIRST: 0,
      SECOND: 1,
      THIRD: 2,
    };
    // Order Enum
    const labelOrder = [order.FIRST, order.SECOND, order.THIRD];
    const LABEL = 0;
    const DATA = 1;
    const data = this.chartData;
    const { onlinePlatforms } = data.latestKpi;
    const sortedPlatforms = Object.entries(onlinePlatforms).sort(
      (a, b) => b[DATA].value - a[DATA].value
    );
    // Add Ordered Configs
    const datasetConfigs = labelOrder.map((position) => ({
      fill: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderColor:
        cssVars.color.platformColor[sortedPlatforms[position][LABEL]].dark,
      backgroundColor:
        cssVars.color.platformColor[sortedPlatforms[position][LABEL]].dark,
    }));

    // add "Other" Configs
    datasetConfigs.push({
      fill: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderDash: [6, 5],
      borderColor: cssVars.color.platformColor.others.dark,
      backgroundColor: cssVars.color.platformColor.others.dark,
    });
    return datasetConfigs;
  }

  static get configurations() {
    return this.chartDatasetConfigs;
  }

  extractOnlinePlatforms = ({ dataPoints }) => {
    const LABEL = 0; // Represents the subtype from online platforms
    const FIRST = 0; // First element in the list
    const DATA = 1; // Position where Data of subtype can be found
    const TOP_THREE = 3; // First three are extracted differently than last one
    const lastPoint = dataPoints[dataPoints.length - 1].onlinePlatforms;
    const datasets = [];
    // Determines order since there will only be 4 datasets (includes Others)
    const sortedPlatforms = Object.entries(lastPoint).sort(
      (a, b) => b[DATA].value - a[DATA].value
    );
    // This will create 3 datasets of the highest data
    for (let i = 0; i < TOP_THREE; i++) {
      const data = [];
      dataPoints.forEach((point) => {
        data.push(point.onlinePlatforms[sortedPlatforms[FIRST][LABEL]].value);
      });
      // Consumed, remove from the platforms list, this helps to aggregate remaining
      sortedPlatforms.shift();
      datasets.push(data);
    }

    const othersData = [];
    // This will create one dataset with others aggregated
    dataPoints.forEach((point) => {
      let otherSum = 0;
      sortedPlatforms.forEach((platform) => {
        otherSum += point.onlinePlatforms[platform[LABEL]].value;
      });
      othersData.push(otherSum);
    });
    datasets.push(othersData);
    return datasets;
  };

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

const chartNameToClassName = {
  CURRENT_STR_PROPERTIES: CurrentStrPropertiesDataBuilder,
  OCCUPANCY: OccupancyDataBuilder,
  REVENUE: RevenueDataBuilder,
  AVG_DAILY_RATE: AverageDailyRateDataBuilder,
  REGISTRATION_RATE: RegistrationRateDataBuilder,
  RENTAL_SIZE: RentalSizeDataBuilder,
  TRANSIENT_OCCUPANCY_TAX: TOTDataBuilder,
  RESIDENTIAL_TYPE: ResidentialTypeDataBuilder,
  RENTAL_STRUCTURE: RentalStructureDataBuilder,
  RENTAL_TYPE: RentalTypeDataBuilder,
  RENTAL_SIZE_SIDEPANEL: RentalSizeSidePanelDataBuilder,
  ONLINE_PLATFORMS: OnlinePlatformsDataBuilder,
  STACKED_BOOKED_RATIO: BookedRatioDataBuilder,
};

export function DataBuilderFactory(desiredChart, data, chartMode) {
  const DataBuilder = chartNameToClassName[desiredChart];
  const initDataBuilder = new DataBuilder(data, chartMode);
  return initDataBuilder;
}
